import { QueryClient } from '@tanstack/react-query';
import { createRouter } from '@tanstack/react-router';
import type { CrmClient } from './api/client.ts';
import { RouteErrorPanel } from './components/route-error-boundary.tsx';
import { toClientError } from './lib/errors.ts';
import { routeTree } from './routeTree.gen.ts';

export function createAppQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        // The server marks what is safe to replay (`AppError.retryable`), and
        // `@crm/api-client` already retries those inside one call with jittered
        // backoff. Retrying again at this layer would multiply the attempts and
        // hammer a struggling API — so this defers to the transport.
        retry: (failureCount, error) => failureCount < 1 && toClientError(error).retryable,
        refetchOnWindowFocus: false,
      },
      mutations: {
        // Never automatic: a mutation retry belongs to the outbox, which owns the
        // idempotency key and tells the user what it is doing.
        retry: false,
      },
    },
  });
}

export function createAppRouter(deps: { queryClient: QueryClient; crm: CrmClient }) {
  return createRouter({
    routeTree,
    context: { queryClient: deps.queryClient, crm: deps.crm },
    defaultErrorComponent: RouteErrorPanel,
    defaultPreload: 'intent',
    // The query cache already holds the data; the router should not keep a
    // second, staler copy of it.
    defaultPreloadStaleTime: 0,
    scrollRestoration: true,
  });
}

export type AppRouter = ReturnType<typeof createAppRouter>;

declare module '@tanstack/react-router' {
  interface Register {
    router: AppRouter;
  }
}
